import { useMutation, useQuery, UseQueryOptions } from "react-query";
import HttpService from "../../services/http";
import { AxiosBasicCredentials, AxiosError } from "axios";
import { toast } from "react-toastify";
import { queryKeys } from "../../constants/queryKeys";
import { queryClient } from "../../providers/QueryProvider";
import { useTranslation } from "react-i18next";
import SecureStorage from "../../utils/secureStorage";
import { UniversalCacheManager } from "../../utils/cacheManager";
import { ownerDataCacheKey, ownerDataQueryKey } from "./ownerData";
import { useLmcUserView } from "../../contexts/AuthContext";
import { GAMING_HISTORY, GameHistoryRecord } from "../../constants/types";

export const entityCache = new UniversalCacheManager();

const defaultOnError = (err: AxiosError<any, any>) => {
  toast.error(err.response?.data?.message || err.message);
};

const isAuthRejection = (err: AxiosError<any, any>): boolean => {
  const status = err.response?.status;
  return status === 401 || status === 403;
};

type EntityPreferencesProp = {
  customer_identification: string;
  entityIdentification: string;
  userIdentification?: string;
  key?: string;
  value?: any;
};

type EntityStatusProp = {
  customer_identification: string;
  entity_identification: string;
  shouldEnable?: boolean;
};

type UpdateEntityProp = {
  customer_identification: string;
  entity_identification: string;
  user_identification?: string;
  key?: string;
  data?: any;
};

export const useGetEntityPreferencesWithSpecialKey = ({
  customer_identification,
  entityIdentification,
  userIdentification,
  key,
}: EntityPreferencesProp) => {
  return useQuery<any, any, any>(
    [
      queryKeys.entityPreferences,
      customer_identification,
      entityIdentification,
      userIdentification,
      key,
    ],
    async () => {
      const cacheKey = `user_preferences_${userIdentification}_${key}`;
      const cached = entityCache.get(cacheKey);

      if (cached) {
        return cached;
      }

      const data = await HttpService.get(
        `/${customer_identification}/${entityIdentification}/user/${userIdentification}/preferences/${key}`
      ).then((response) => {
        return response?.data?.element;
      });

      entityCache.setWithLongTTL(cacheKey, data);
      return data;
    },
    {
      onError: (err: AxiosError<any, any>) => {
        toast.error(err.response?.data?.message || err.message);
      },
    }
  );
};

export const useCreateEntityPreferencesWithSpecialKey = () => {
  return useMutation<any, AxiosError<any, any>, any>(
    ({
      customer_identification,
      entityIdentification,
      userIdentification,
      key,
      value,
    }: EntityPreferencesProp) =>
      HttpService.post(
        `/${customer_identification}/${entityIdentification}/user/${userIdentification}/preferences/${key}`,
        { user_data_preferences: value }
      ),
    {
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries([
          "user-preferences",
          variables.userIdentification,
        ]);

        queryClient.invalidateQueries([
          queryKeys.entityPreferences,
          variables.customer_identification,
          variables.entityIdentification,
          variables.userIdentification,
          variables.key,
        ]);

        const cacheKey = `user_preferences_${variables.userIdentification}_${variables.key}`;
        entityCache.delete(cacheKey);
      },
      onError: defaultOnError,
    }
  );
};

export const useDeleteEntityPreferencesWithSpecialKey = () => {
  return useMutation<any, AxiosError<any, any>, any>(
    ({
      customer_identification,
      entityIdentification,
      userIdentification,
      key,
    }: EntityPreferencesProp) =>
      HttpService.delete(
        `/${customer_identification}/${entityIdentification}/user/${userIdentification}/preferences/${key}`
      ),
    {
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries([
          "user-preferences",
          variables.userIdentification,
        ]);

        queryClient.invalidateQueries([
          queryKeys.entityPreferences,
          variables.customer_identification,
          variables.entityIdentification,
          variables.userIdentification,
          variables.key,
        ]);

        const cacheKey = `user_preferences_${variables.userIdentification}_${variables.key}`;
        entityCache.delete(cacheKey);
      },
      onError: defaultOnError,
    }
  );
};

export const useGetEntity = (
  customer_identification: string,
  entityIdentification: string,
  options?: UseQueryOptions<
    any,
    AxiosError<any, any>,
    any,
    [string, string, string]
  >
) => {
  return useQuery(
    ["entities", customer_identification, entityIdentification],
    async () => {
      const cacheKey = `entity_${customer_identification}_${entityIdentification}`;
      const cached = entityCache.get(cacheKey);

      if (cached) {
        return cached;
      }

      const res = await HttpService.get(
        `/${customer_identification}/${entityIdentification}/entity`
      );

      const data = res?.data?.element;
      entityCache.setWithMediumTTL(cacheKey, data);
      return data;
    },
    {
      enabled: !!customer_identification && !!entityIdentification,
      onError: defaultOnError,
      ...options,
    }
  );
};

export const useGetUserData = (
  customer_identification: string,
  entityIdentification: string,
  customerId: string,
  options?: UseQueryOptions<
    any,
    AxiosError<any, any>,
    any,
    [string, string, string, string]
  >
) => {
  return useQuery(
    ["user-data", customer_identification, entityIdentification, customerId],
    async () => {
      const cacheKey = `user_data_${customer_identification}_${entityIdentification}_${customerId}`;
      const cached = entityCache.get(cacheKey);
      if (cached) {
        return cached;
      }

      const res = await HttpService.get(
        `/${customer_identification}/${entityIdentification}/user/${customerId}/data`
      );

      const data = res?.data?.element;
      entityCache.setWithMediumTTL(cacheKey, data);
      return data;
    },
    {
      enabled:
        !!customer_identification && !!entityIdentification && !!customerId,
      onError: defaultOnError,
      ...options,
    }
  );
};

export const useGetUserProfileData = (
  customer_identification: string,
  entityIdentification: string,
  customerId: string,
  options?: UseQueryOptions<
    any,
    AxiosError<any, any>,
    any,
    [string, string, string, string]
  >
) => {
  return useQuery(
    [
      "user-profile-data",
      customer_identification,
      entityIdentification,
      customerId,
    ],
    async () => {
      const cacheKey = `user_profile_data_${customer_identification}_${entityIdentification}_${customerId}`;
      const cached = entityCache.get(cacheKey);
      if (cached) {
        return cached;
      }

      const res = await HttpService.get(
        `/${customer_identification}/${entityIdentification}/user/${customerId}/profile-data`
      );

      const data = res?.data?.element;
      entityCache.setWithMediumTTL(cacheKey, data);
      return data;
    },
    {
      enabled:
        !!customer_identification && !!entityIdentification && !!customerId,
      onError: defaultOnError,
      ...options,
    }
  );
};

export const useGetEntityStatus = (
  customer_identification: string,
  entityIdentification: string,
  options?: UseQueryOptions<
    any,
    AxiosError<any, any>,
    any,
    [string, string, string]
  >
) => {
  return useQuery(
    ["entity-stats", customer_identification, entityIdentification],
    async () => {
      const cacheKey = `entity_status_${customer_identification}_${entityIdentification}`;
      const cached = entityCache.get(cacheKey);

      if (cached) {
        return cached;
      }

      const res = await HttpService.get(
        `/${customer_identification}/${entityIdentification}/entity/status`
      );

      const data = res?.data?.element;
      entityCache.setWithLongTTL(cacheKey, data);
      return data;
    },
    {
      enabled: !!customer_identification && !!entityIdentification,
      onError: defaultOnError,
      ...options,
    }
  );
};

export const useUpdateEntityStatus = () => {
  return useMutation<any, AxiosError<any, any>, EntityStatusProp>(
    ({
      customer_identification,
      entity_identification,
      shouldEnable,
    }: EntityStatusProp) =>
      HttpService.patch(
        `/${customer_identification}/${entity_identification}/entity/status/${
          shouldEnable ? "SERVICE_ON" : "SERVICE_OFF"
        }`,
        null
      ),
    {
      onSuccess: (_data, variables) => {
        entityCache.delete(
          `entity_status_${variables.customer_identification}_${variables.entity_identification}`
        );
        entityCache.delete(
          `entity_check_${variables.customer_identification}_${variables.entity_identification}`
        );

        queryClient.invalidateQueries(["entities"]);
        queryClient.invalidateQueries(["entity-check"]);
        queryClient.invalidateQueries(["entity-stats"]);
        queryClient.refetchQueries("entity-check");
      },
      onError: defaultOnError,
    }
  );
};

export const useExportData = () => {
  return useMutation<any, AxiosError<any, any>, any>(
    ({
      customer_identification,
      entity_identification,
    }: {
      customer_identification: string;
      entity_identification: string;
    }) =>
      HttpService.get(
        `/${customer_identification}/${entity_identification}/entity/user/export`
      ),
    {
      onError: defaultOnError,
    }
  );
};

export type WebhookSecretInfo = {
  secret_preview: string | null;
  algorithm: string | null;
  created_at: string | null;
  last_rotated_at: string | null;
};

export type WebhookSecretRevealed = WebhookSecretInfo & {
  secret: string;
};

export const useFetchWebhookSecret = () => {
  return useMutation<
    WebhookSecretInfo,
    AxiosError<any, any>,
    { customer_identification: string; entity_identification: string }
  >(
    async ({ customer_identification, entity_identification }) => {
      const res = await HttpService.get(
        `/${customer_identification}/${entity_identification}/entity/webhook-secret`
      );
      return (
        res?.data?.element ?? {
          secret_preview: null,
          algorithm: null,
          created_at: null,
          last_rotated_at: null,
        }
      );
    },
    {
      onError: defaultOnError,
    }
  );
};

export const useRotateWebhookSecret = () => {
  return useMutation<
    WebhookSecretRevealed,
    AxiosError<any, any>,
    { customer_identification: string; entity_identification: string }
  >(
    async ({ customer_identification, entity_identification }) => {
      const res = await HttpService.post(
        `/${customer_identification}/${entity_identification}/entity/webhook-secret`,
        {}
      );
      return res?.data?.element;
    },
    {
      onError: defaultOnError,
    }
  );
};

export const useCreateEntityPreferencesByPath = () => {
  return useMutation<any, AxiosError<any, any>, any>(
    ({
      user_identification,
      entity_identification,
      path,
      value,
    }: {
      user_identification: string;
      entity_identification: string;
      path: string;
      value: any;
    }) =>
      HttpService.post(
        `/${user_identification}/${entity_identification}/entity/preferences/${path}`,
        { entity_data_preferences: value }
      ),
    {
      onSuccess: (_res, variables) => {
        entityCache.delete(
          `entity_preferences_${variables.user_identification}_${variables.entity_identification}`
        );
        entityCache.delete(
          `entity_preferences_${variables.user_identification}_${variables.entity_identification}_${variables.path}`
        );
        entityCache.delete(
          `entity_check_${variables.user_identification}_${variables.entity_identification}`
        );
        const dotIndex = (variables.path as string).indexOf(".");
        if (dotIndex !== -1) {
          const parentPath = variables.path.substring(0, dotIndex);
          entityCache.delete(
            `entity_preferences_${variables.user_identification}_${variables.entity_identification}_${parentPath}`
          );
        }

        queryClient.invalidateQueries(["entity-check"]);
        queryClient.invalidateQueries(["entityPreferences"]);
        queryClient.invalidateQueries(["entityPreferencesByPath"]);
        queryClient.refetchQueries("entity-check");
      },
      onError: defaultOnError,
    }
  );
};

export const useGetEntityPreferencesByPath = (
  user_identification: string,
  entity_identification: string,
  path: string,
  options?: UseQueryOptions<
    any,
    AxiosError<any, any>,
    any,
    [string, string, string, string]
  >
) => {
  return useQuery(
    [
      "entityPreferencesByPath",
      user_identification,
      entity_identification,
      path,
    ],
    async () => {
      const cacheKey = `entity_preferences_${user_identification}_${entity_identification}_${path}`;
      const cached = entityCache.get(cacheKey);

      if (cached) {
        return cached;
      }

      const res = await HttpService.get(
        `/${user_identification}/${entity_identification}/entity/preferences/${path}`
      );

      const data = res?.data?.element?.entity_data_preferences;
      entityCache.setWithMediumTTL(cacheKey, data);
      return data;
    },
    {
      enabled: !!user_identification && !!entity_identification && !!path,
      onError: defaultOnError,
      ...options,
    }
  );
};

export const useUpdateEntityPreferencesByPath = () => {
  return useMutation<any, AxiosError<any, any>, any>(
    ({
      user_identification,
      entity_identification,
      path,
      value,
    }: {
      user_identification: string;
      entity_identification: string;
      path: string;
      value: any;
    }) =>
      HttpService.patch(
        `/${user_identification}/${entity_identification}/entity/preferences/${path}`,
        { entity_data_preferences: value }
      ),
    {
      onSuccess: (_res, variables) => {
        entityCache.delete(
          `entity_preferences_${variables.user_identification}_${variables.entity_identification}`
        );
        entityCache.clearByPattern(
          `entity_preferences_${variables.user_identification}_${variables.entity_identification}_`
        );
        entityCache.delete(
          `entity_preferences_${variables.user_identification}_${variables.entity_identification}_${variables.path}`
        );
        entityCache.delete(
          `entity_check_${variables.user_identification}_${variables.entity_identification}`
        );
        const dotIndex = (variables.path as string).indexOf(".");
        if (dotIndex !== -1) {
          const parentPath = variables.path.substring(0, dotIndex);
          entityCache.delete(
            `entity_preferences_${variables.user_identification}_${variables.entity_identification}_${parentPath}`
          );
        }

        queryClient.invalidateQueries(["entity-check"]);
        queryClient.invalidateQueries(["entityPreferences"]);
        queryClient.invalidateQueries(["entityPreferencesByPath"]);
        queryClient.refetchQueries("entity-check");
      },
      onError: defaultOnError,
    }
  );
};

export const useDeleteEntityPreferencesByPath = () => {
  return useMutation<any, AxiosError<any, any>, any>(
    ({
      user_identification,
      entity_identification,
      path,
    }: {
      user_identification: string;
      entity_identification: string;
      path: string;
    }) =>
      HttpService.delete(
        `/${user_identification}/${entity_identification}/entity/preferences/${path}`
      ),
    {
      onSuccess: (_res, variables) => {
        entityCache.delete(
          `entity_preferences_${variables.user_identification}_${variables.entity_identification}`
        );
        entityCache.clearByPattern(
          `entity_preferences_${variables.user_identification}_${variables.entity_identification}_`
        );
        entityCache.delete(
          `entity_preferences_${variables.user_identification}_${variables.entity_identification}_${variables.path}`
        );
        entityCache.delete(
          `entity_check_${variables.user_identification}_${variables.entity_identification}`
        );
        const dotIndex = (variables.path as string).indexOf(".");
        if (dotIndex !== -1) {
          const parentPath = variables.path.substring(0, dotIndex);
          entityCache.delete(
            `entity_preferences_${variables.user_identification}_${variables.entity_identification}_${parentPath}`
          );
        }

        queryClient.invalidateQueries(["entity-check"]);
        queryClient.invalidateQueries(["entityPreferences"]);
        queryClient.invalidateQueries(["entityPreferencesByPath"]);
        queryClient.refetchQueries("entity-check");
      },
      onError: defaultOnError,
    }
  );
};

export const useUpdateEntity = () => {
  const { t } = useTranslation();

  return useMutation<any, AxiosError<any, any>, any>(
    ({
      customer_identification,
      entity_identification,
      data,
    }: UpdateEntityProp) =>
      HttpService.patch(
        `/${customer_identification}/${entity_identification}/entity`,
        data
      ),
    {
      onSuccess: (_res, variables) => {
        queryClient.invalidateQueries(["entities"]);
        entityCache.delete(
          `entity_${variables.customer_identification}_${variables.entity_identification}`
        );
        toast.success(t("dashboard.entities.updateEntity.success"));
      },
      onError: defaultOnError,
    }
  );
};

export const useGetLMCProcessMutation = () => {
  return useMutation<any, AxiosError<any, any>, any>(
    ({ customer_identification, entity_identification }: UpdateEntityProp) =>
      HttpService.get(
        `/${customer_identification}/${entity_identification}/entity/process/lmc/identification`
      ),
    { onError: defaultOnError }
  );
};

export const useUpdateLMCProcess = () => {
  return useMutation<any, AxiosError<any, any>, any>(
    ({
      entity_identification,
      customer_identification,
      user_identification,
      data,
    }: UpdateEntityProp) =>
      HttpService.patch(
        `/${customer_identification}/entity/${entity_identification}/${user_identification}/lmc`,
        data
      ),
    {
      onMutate: async (variables) => {
        const queryKey = [
          "entityPreferences",
          variables.customer_identification,
          variables.entity_identification,
        ];
        await queryClient.cancelQueries({ queryKey });
        const previousData = queryClient.getQueryData(queryKey);
        queryClient.setQueryData(queryKey, (old: any = {}) => ({
          ...old,
          lmc_form: {
            ...(old.lmc_form ?? {}),
            ...variables.data,
          },
        }));
        return { previousData };
      },
      onSuccess: (_res, variables) => {
        entityCache.delete(
          `entity_preferences_${variables.customer_identification}_${variables.entity_identification}`
        );
        entityCache.delete(
          `entity_preferences_${variables.customer_identification}_${variables.entity_identification}_lmc_form`
        );

        queryClient.invalidateQueries(["entityPreferencesByPath"]);
        queryClient.invalidateQueries(["entityPreferences"]);
        queryClient.invalidateQueries(["process-data"]);
        queryClient.invalidateQueries(["process-ui-data"]);
      },
      onError: (err, variables, context: any) => {
        if (context?.previousData) {
          queryClient.setQueryData(
            [
              "entityPreferences",
              variables.customer_identification,
              variables.entity_identification,
            ],
            context.previousData
          );
        }
        defaultOnError(err);
      },
    }
  );
};

export const useGetProcessData = (
  login_code: string,
  options?: UseQueryOptions<any, AxiosError<any, any>, any, [string, string]>
) => {
  return useQuery(
    ["process-data", login_code],
    async () => {
      const cacheKey = `entity_process_${login_code}`;
      const cached = entityCache.get(cacheKey);

      if (cached) {
        return cached;
      }
      const res = await HttpService.get(`/entity/process/${login_code}`);
      const data = res?.data?.element;

      entityCache.setWithLongTTL(cacheKey, data);
      return data;
    },
    {
      enabled: !!login_code,
      onError: defaultOnError,
      ...options,
    }
  );
};

export const useGetProcessUIData = (
  login_code: string,
  options?: UseQueryOptions<any, AxiosError<any, any>, any, [string, string]>
) => {
  return useQuery(
    ["process-ui-data", login_code],
    async () => {
      const cacheKey = `entity_process_ui_${login_code}`;
      const cached = entityCache.get(cacheKey);

      if (cached) {
        return cached;
      }
      const res = await HttpService.get(`/entity/ui/process/${login_code}`);
      const data = res?.data?.element;

      entityCache.setWithLongTTL(cacheKey, data);
      return data;
    },
    {
      enabled: !!login_code,
      onError: defaultOnError,
      ...options,
    }
  );
};

/** The page signing in tells the person why a sign-in fails, in its own language. */
export const useLoginEntityUser = () => {
  return useMutation<any, AxiosError<any, any>, any>(
    ({
      entity_identification,
      data,
      redirect,
    }: UpdateEntityProp & { redirect?: string }) => {
      const query = redirect ? `?redirect=${encodeURIComponent(redirect)}` : "";
      return HttpService.post(
        `/auth/${entity_identification}/login-page/login${query}`,
        data
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["entityPreferences"]);
      },
      onError: (err) => {
        if (isAuthRejection(err)) {
          SecureStorage.clearAuthData();
        }
      },
    }
  );
};

/** As for the password: the page tells why the sign-in fails. */
export const useLoginPageSocialLogin = () => {
  return useMutation<any, AxiosError<any, any>, any>(
    ({
      entity_identification,
      data,
      redirect,
    }: {
      entity_identification: string;
      data: { type: "login"; providerPayload: any };
      redirect?: string;
    }) => {
      const query = redirect ? `?redirect=${encodeURIComponent(redirect)}` : "";
      return HttpService.post(
        `/auth/${entity_identification}/login-page/social-login${query}`,
        data
      );
    }
  );
};

export const useUpdateEmail = () => {
  return useMutation<any, AxiosError<any, any>, any>(
    ({
      customer_identification,
      entity_identification,
      data,
    }: UpdateEntityProp) =>
      HttpService.patch(
        `/${customer_identification}/${entity_identification}/entity/update-email`,
        data
      ),
    {
      onSuccess: (data) => {
        toast.success(data?.data?.message || "Validation link is sent");
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || err.message);
      },
    }
  );
};

type EmailSenderValidationProp = {
  customer_identification: string;
  entity_identification: string;
};

export type EmailSenderValidationStatus = {
  email: string | null;
  is_valid: boolean;
  validated_at: string | null;
  code_expires_at: string | null;
};

export const useEmailSenderValidationStatus = (
  customer_identification: string,
  entity_identification: string,
  options?: UseQueryOptions<
    EmailSenderValidationStatus,
    AxiosError<any, any>,
    EmailSenderValidationStatus,
    [string, string, string]
  >
) => {
  return useQuery<
    EmailSenderValidationStatus,
    AxiosError<any, any>,
    EmailSenderValidationStatus,
    [string, string, string]
  >(
    [
      "email-sender-validation-status",
      customer_identification,
      entity_identification,
    ],
    async () => {
      const res = await HttpService.get(
        `/${customer_identification}/${entity_identification}/entity/email-sender/validation/status`
      );
      return res?.data?.element as EmailSenderValidationStatus;
    },
    {
      enabled: !!customer_identification && !!entity_identification,
      staleTime: 10 * 1000,
      onError: (err) => {
        console.warn(
          "email sender validation status fetch failed:",
          err.response?.data?.message || err.message
        );
      },
      ...options,
    }
  );
};

export const useRequestEmailSenderValidation = () => {
  return useMutation<
    any,
    AxiosError<any, any>,
    EmailSenderValidationProp & { email?: string }
  >(
    ({ customer_identification, entity_identification, email }) =>
      HttpService.post(
        `/${customer_identification}/${entity_identification}/entity/email-sender/validation/request`,
        email ? { email } : {}
      ),
    {
      onSuccess: (res, variables) => {
        const fresh = res?.data?.element as
          | EmailSenderValidationStatus
          | undefined;
        const queryKey = [
          "email-sender-validation-status",
          variables.customer_identification,
          variables.entity_identification,
        ] as const;
        if (fresh) {
          queryClient.setQueryData(queryKey, fresh);
        } else {
          queryClient.invalidateQueries(queryKey);
        }
      },
      onError: defaultOnError,
    }
  );
};

export const useConfirmEmailSenderValidation = () => {
  const { t } = useTranslation();

  return useMutation<
    any,
    AxiosError<any, any>,
    EmailSenderValidationProp & { code: string }
  >(
    ({ customer_identification, entity_identification, code }) =>
      HttpService.post(
        `/${customer_identification}/${entity_identification}/entity/email-sender/validation/confirm`,
        { code }
      ),
    {
      onSuccess: (_res, variables) => {
        queryClient.invalidateQueries([
          "email-sender-validation-status",
          variables.customer_identification,
          variables.entity_identification,
        ]);
        queryClient.invalidateQueries(["entity-check"]);
        toast.success(
          t("dashboard.entities.emailSenderValidation.successValidated")
        );
      },
      onError: defaultOnError,
    }
  );
};

export type EmailDomainDnsRecord = {
  key: string;
  host_name: string;
  type: string;
  value: string;
  status: boolean;
};

export type EmailDomainStatus = {
  enabled: boolean;
  domain: string | null;
  status: "pending" | "verified" | "failed" | null;
  dns_records: EmailDomainDnsRecord[];
  created_at: string | null;
  verified_at: string | null;
  last_checked_at: string | null;
  last_error_code: string | null;
  last_error: string | null;
};

const emailDomainQueryKey = (
  customer_identification: string,
  entity_identification: string
) =>
  [
    "email-domain-status",
    customer_identification,
    entity_identification,
  ] as const;

const applyEmailDomainResult = (
  res: any,
  variables: EmailSenderValidationProp
) => {
  const fresh = res?.data?.element as EmailDomainStatus | undefined;
  const queryKey = emailDomainQueryKey(
    variables.customer_identification,
    variables.entity_identification
  );
  if (fresh) {
    queryClient.setQueryData(queryKey, fresh);
  } else {
    queryClient.invalidateQueries(queryKey);
  }
};

export const useEmailDomainStatus = (
  customer_identification: string,
  entity_identification: string,
  options?: UseQueryOptions<
    EmailDomainStatus,
    AxiosError<any, any>,
    EmailDomainStatus,
    readonly [string, string, string]
  >
) => {
  return useQuery<
    EmailDomainStatus,
    AxiosError<any, any>,
    EmailDomainStatus,
    readonly [string, string, string]
  >(
    emailDomainQueryKey(customer_identification, entity_identification),
    async () => {
      const res = await HttpService.get(
        `/${customer_identification}/${entity_identification}/entity/email-domain`
      );
      return res?.data?.element as EmailDomainStatus;
    },
    {
      enabled: !!customer_identification && !!entity_identification,
      staleTime: 30 * 1000,
      onError: (err) => {
        console.warn(
          "email domain status fetch failed:",
          err.response?.data?.message || err.message
        );
      },
      ...options,
    }
  );
};

const useEmailDomainErrorToast = () => {
  const { t } = useTranslation();
  return (err: AxiosError<any, any>) => {
    const code = err.response?.data?.element?.error_code;
    const message = code
      ? (t(`dashboard.entities.emailDomain.errors.${code}`, {
          defaultValue:
            err.response?.data?.message ||
            (t(
              "dashboard.entities.emailDomain.errors.service_unavailable"
            ) as string),
        }) as string)
      : err.response?.data?.message || err.message;
    toast.error(message);
  };
};

export const useCreateEmailDomain = () => {
  const onError = useEmailDomainErrorToast();
  return useMutation<
    any,
    AxiosError<any, any>,
    EmailSenderValidationProp & { domain: string }
  >(
    ({ customer_identification, entity_identification, domain }) =>
      HttpService.post(
        `/${customer_identification}/${entity_identification}/entity/email-domain`,
        { domain }
      ),
    {
      onSuccess: applyEmailDomainResult,
      onError,
    }
  );
};

export const useVerifyEmailDomain = () => {
  const onError = useEmailDomainErrorToast();
  return useMutation<any, AxiosError<any, any>, EmailSenderValidationProp>(
    ({ customer_identification, entity_identification }) =>
      HttpService.post(
        `/${customer_identification}/${entity_identification}/entity/email-domain/verify`,
        {}
      ),
    {
      onSuccess: applyEmailDomainResult,
      onError,
    }
  );
};

export const useDeleteEmailDomain = () => {
  const onError = useEmailDomainErrorToast();
  return useMutation<any, AxiosError<any, any>, EmailSenderValidationProp>(
    ({ customer_identification, entity_identification }) =>
      HttpService.delete(
        `/${customer_identification}/${entity_identification}/entity/email-domain`
      ),
    {
      onSuccess: applyEmailDomainResult,
      onError,
    }
  );
};

export const useResendValidationEmail = () => {
  return useMutation<any, AxiosError<any, any>, any>(
    ({ customer_identification, entity_identification }: UpdateEntityProp) =>
      HttpService.post(
        `/${customer_identification}/${entity_identification}/entity/resend-validation-email`,
        null
      ),
    {
      onSuccess: (response) => {
        const message = response?.data?.message;
        const userEmail = response?.data?.element?.user_email;
        if (message) {
          toast.success(message);
        } else if (userEmail) {
          toast.success(
            `Validation email is sent to your current email address: ${userEmail}`
          );
        } else {
          toast.success(
            "Validation email is sent to your current email address."
          );
        }
      },
      onError: defaultOnError,
    }
  );
};

export const useGetCheckEntity = (
  customer_identification: string,
  entity_identification: string,
  options?: UseQueryOptions<
    any,
    AxiosError<any, any>,
    any,
    [string, string, string]
  >
) => {
  return useQuery(
    ["entity-check", customer_identification, entity_identification],
    async () => {
      const cacheKey = `entity_check_${customer_identification}_${entity_identification}`;
      const cached = entityCache.get(cacheKey);

      if (cached) {
        return cached;
      }

      const res = await HttpService.get(
        `/${customer_identification}/${entity_identification}/entity/check`
      );

      const data = res?.data?.element;
      entityCache.setWithLongTTL(cacheKey, data);
      return data;
    },
    {
      enabled: !!customer_identification && !!entity_identification,
      onError: defaultOnError,
      ...options,
    }
  );
};

export const useGetStatistic = (
  customer_identification: string,
  entityIdentification: string,
  itt: number
) => {
  return useQuery(
    [
      "statistics",
      customer_identification,
      entityIdentification,
      `past_${itt}_days`,
    ],
    async () => {
      const cacheKey = `entity_user_statistics_${customer_identification}_${entityIdentification}_${itt}days`;
      const cached = entityCache.get(cacheKey);

      if (cached) {
        return cached;
      }

      const res = await HttpService.get(
        `/${customer_identification}/${entityIdentification}/entity/user-statistics?days=${itt}`
      );

      const data = res?.data?.element;
      entityCache.setWithMediumTTL(cacheKey, data);
      return data;
    },
    {
      enabled: !!customer_identification && !!entityIdentification,
      onError: defaultOnError,
    }
  );
};

export const useGetEntityOwnerData = (
  customer_identification: string,
  entityIdentification: string,
  key: string,
  options?: UseQueryOptions<
    any,
    AxiosError<any, any>,
    any,
    [string, string, string, string]
  >
) => {
  return useQuery(
    ownerDataQueryKey(customer_identification, entityIdentification, key),
    async () => {
      const cacheKey = ownerDataCacheKey(
        customer_identification,
        entityIdentification,
        key
      );
      const cached = entityCache.get(cacheKey);

      if (cached) {
        return cached;
      }

      const res = await HttpService.get(
        `/${customer_identification}/${entityIdentification}/entity/data/${key}`
      );

      const data = res?.data?.element;
      entityCache.setWithLongTTL(cacheKey, data);
      return data;
    },
    {
      enabled: !!customer_identification && !!entityIdentification && !!key,
      onError: defaultOnError,
      ...options,
    }
  );
};

export const useGetEntityData = (
  customer_identification: string,
  entityIdentification: string,
  options?: UseQueryOptions<
    any,
    AxiosError<any, any>,
    any,
    [string, string, string]
  >
) => {
  return useQuery(
    ["entity-data", customer_identification, entityIdentification],
    async () => {
      const res = await HttpService.get(
        `/${customer_identification}/${entityIdentification}/entity/data`
      );
      return res?.data?.element;
    },
    {
      enabled: !!customer_identification && !!entityIdentification,
      onError: defaultOnError,
      ...options,
    }
  );
};

export const useUpdateEntityData = () => {
  const { t } = useTranslation();

  return useMutation<any, AxiosError<any, any>, any>(
    ({
      customer_identification,
      entity_identification,
      data,
    }: UpdateEntityProp) =>
      HttpService.patch(
        `/${customer_identification}/${entity_identification}/entity/data`,
        data
      ),
    {
      onSuccess: async (_res, variables) => {
        await queryClient.invalidateQueries(["entity-data"]);
        toast.success(t("dashboard.entities.updateEntity.success"));
      },
      onError: defaultOnError,
    }
  );
};

export const useUpdateEntityOwnerData = () => {
  return useMutation<any, AxiosError<any, any>, any>(
    ({
      customer_identification,
      entity_identification,
      key,
      data,
    }: UpdateEntityProp) =>
      HttpService.patch(
        `/${customer_identification}/${entity_identification}/entity/data/${key}`,
        { value: data }
      ),
    {
      onSuccess: (_res, variables) => {
        entityCache.delete(
          `entity_data_${variables.customer_identification}_${variables.entity_identification}_${variables.key}`
        );
        queryClient.invalidateQueries(["entity-owner-data"]);
      },
      onError: defaultOnError,
    }
  );
};

export const useGetLoginFormActive = (
  customer_identification: string,
  entityIdentification: string,
  options?: UseQueryOptions<
    any,
    AxiosError<any, any>,
    any,
    [string, string, string]
  >
) => {
  return useQuery(
    ["login-form-active", customer_identification, entityIdentification],
    async () => {
      const cacheKey = `login_form_active_${customer_identification}_${entityIdentification}`;
      const cached = entityCache.get(cacheKey);

      if (cached) {
        return cached;
      }

      const res = await HttpService.get(
        `/${customer_identification}/${entityIdentification}/entity/login-form`
      );

      const data = res?.data?.element;
      entityCache.setWithMediumTTL(cacheKey, data);
      return data;
    },
    {
      enabled: !!customer_identification && !!entityIdentification,
      onError: defaultOnError,
      ...options,
    }
  );
};

export const useUpdateLoginFormActive = () => {
  return useMutation<any, AxiosError<any, any>, any>(
    ({
      customer_identification,
      entity_identification,
      is_active,
    }: {
      customer_identification: string;
      entity_identification: string;
      is_active: boolean;
    }) =>
      HttpService.patch(
        `/${customer_identification}/${entity_identification}/entity/login-form`,
        { is_active }
      ),
    {
      onSuccess: (_res, variables) => {
        entityCache.delete(
          `login_form_active_${variables.customer_identification}_${variables.entity_identification}`
        );
        queryClient.invalidateQueries(["login-form-active"]);
      },
      onError: defaultOnError,
    }
  );
};

export const useUpdateShareState = () => {
  return useMutation<any, AxiosError<any, any>, any>(
    ({
      customer_identification,
      entity_identification,
    }: {
      customer_identification: string;
      entity_identification: string;
    }) =>
      HttpService.get(
        `/${customer_identification}/${entity_identification}/entity/process`
      ),
    { onError: defaultOnError }
  );
};

export const useGetProcess = (
  customer_identification: string,
  entity_identification: string
) => {
  return useQuery(
    ["process", customer_identification, entity_identification],
    async () => {
      const response = await HttpService.get(
        `/${customer_identification}/${entity_identification}/entity/process`
      );
      return response?.data?.element;
    },
    {
      enabled: !!customer_identification && !!entity_identification,
      onError: defaultOnError,
    }
  );
};

export const useSetLoginFormObject = () => {
  return useMutation<any, AxiosError<any, any>, any>(
    ({
      customer_identification,
      entity_identification,
      formObject,
    }: {
      customer_identification: string;
      entity_identification: string;
      formObject: any;
    }) =>
      HttpService.patch(
        `/${customer_identification}/${entity_identification}/entity/login-form/preferences`,
        formObject
      ),
    {
      onSuccess: (_res, variables) => {
        entityCache.delete(
          `login_form_active_${variables.customer_identification}_${variables.entity_identification}`
        );
        queryClient.invalidateQueries(["login-form-active"]);
      },
      onError: defaultOnError,
    }
  );
};

export const useGetLMCUserProfile = (
  entity_identification: string,
  userIdentification: string,
  options?: UseQueryOptions<
    any,
    AxiosError<any, any>,
    any,
    [string, string, string]
  >
) => {
  const {
    state: { auth },
    dispatch,
  } = useLmcUserView();
  const { onError: callerOnError, ...restOptions } = options || {};

  return useQuery(
    ["lmc-user-profile", entity_identification, userIdentification],
    async () => {
      const res = await HttpService.get(
        `/entity/${entity_identification}/process/${userIdentification}/LMC/data`,
        {
          headers: {
            Authorization: `Basic ${btoa(
              `${auth.user_identification}:${auth.access_token}`
            )}`,
          },
        }
      );
      return res?.data?.element;
    },
    {
      retry: (failureCount, error) => {
        if (
          error?.response?.data?.code === "RSUSKO026" ||
          isAuthRejection(error)
        ) {
          return false;
        }
        return failureCount < 3;
      },
      onError: (err) => {
        if (err?.response?.data?.code === "RSUSKO026" || isAuthRejection(err)) {
          dispatch({ type: "LOGOUT", payload: {} });
          return;
        }
        if (callerOnError) {
          callerOnError(err);
        } else {
          toast.error(err.response?.data?.message || err.message);
        }
      },
      ...restOptions,
    }
  );
};

export const useGetSignupFormsList = (
  entity_identification: string,
  options?: UseQueryOptions<any, AxiosError<any, any>, any, [string, string]>
) => {
  return useQuery(
    ["signupFormsList", entity_identification],
    async () => {
      const res = await HttpService.get(
        `/link-registration/${entity_identification}/signup-forms`
      );
      return res?.data?.element;
    },
    {
      enabled: !!entity_identification,
      onError: defaultOnError,
      ...options,
    }
  );
};

export const useGetEntityBasicInfo = (
  entity_identification: string,
  options?: UseQueryOptions<any, AxiosError<any, any>, any, [string, string]>
) => {
  return useQuery(
    ["getEntityBasicInfo", entity_identification],
    async () => {
      const res = await HttpService.get(
        `/link-registration/${entity_identification}/service-basic-info`
      );
      return res?.data?.element;
    },
    {
      enabled: !!entity_identification,
      onError: defaultOnError,
      ...options,
    }
  );
};

export const useOwnderLoginSocial = () => {
  return useMutation<any, AxiosError<any, any>, any>(
    ({
      data,
    }: {
      data: {
        providerPayload: { access_token: string; provider: string };
        type: string;
        user_first_name?: string;
        user_last_name?: string;
        user_country_code?: string;
        entity_name?: string;
        entity_description?: string;
        user_birth_date?: string;
        user_user_name?: string;
        entity_id: string;
      };
    }) => HttpService.post(`/auth/social/login/owner`, data),
    {
      onError: (err) => {
        if (isAuthRejection(err)) {
          SecureStorage.clearOAuthCredentials();
        }
        defaultOnError(err);
      },
    }
  );
};

export const useCheckOwnerSocialAuth = () => {
  return useMutation<any, AxiosError<any, any>, any>(
    ({
      data,
    }: {
      data: {
        providerPayload: { access_token: string; provider: string };
      };
    }) => HttpService.post(`/auth/social/login/owner/check`, data),
    {
      onError: (err) => {
        if (isAuthRejection(err)) {
          SecureStorage.clearOAuthCredentials();
        }
        defaultOnError(err);
      },
    }
  );
};

export const useOwnderLogin = () => {
  return useMutation<any, AxiosError<any, any>, any>(
    ({ credentials }: { credentials: any }) =>
      HttpService.post("/auth/login", credentials),
    {
      onError: (err) => {
        if (isAuthRejection(err)) {
          SecureStorage.clearAuthData();
        }
        defaultOnError(err);
      },
    }
  );
};

export const useOwnderSendValidationLink = () => {
  const { t } = useTranslation();

  return useMutation<any, AxiosError<any, any>, any>(
    ({ user_identification }: { user_identification: string }) =>
      HttpService.post(`/auth/${user_identification}/validation-link`),
    {
      onSuccess: () => {
        toast.success(t("login.msg"));
      },
      onError: defaultOnError,
    }
  );
};

export const useOwnderRegister = () => {
  return useMutation<any, AxiosError<any, any>, any>(
    ({ data }: { data: any }) => HttpService.post("/auth/register", data),
    { onError: defaultOnError }
  );
};

export const useOwnderForgotPassword = () => {
  const { t } = useTranslation();
  return useMutation<any, AxiosError<any, any>, any>(
    (user_email: string) =>
      HttpService.get(
        `/auth/reset-link?email_address=${encodeURIComponent(user_email)}`
      ),
    {
      onSuccess: () => {
        toast.success(t("forgotPassword.msg1"));
      },
      onError: defaultOnError,
    }
  );
};

export const useModifyWidgetCallbot = () => {
  return useMutation<any, AxiosError<any, any>, any>(
    ({
      entity_identification,
      customer_identification,
      data,
    }: UpdateEntityProp) =>
      HttpService.post(
        `/${customer_identification}/${entity_identification}/entity/widget/call-bot`,
        data
      ),
    {
      onSuccess: (_res, variables) => {
        entityCache.delete(
          `entity_preferences_${variables.customer_identification}_${variables.entity_identification}`
        );
        queryClient.invalidateQueries(["entityPreferences"]);
        queryClient.invalidateQueries(["lmc-widget-bot"]);
      },
      onError: defaultOnError,
    }
  );
};

export const useGetCallBot = (
  entity_identification: string,
  customer_identification: string,
  bot_id: string,
  options?: UseQueryOptions<
    any,
    AxiosError<any, any>,
    any,
    [string, string, string, string]
  >
) => {
  return useQuery(
    ["lmc-widget-bot", customer_identification, entity_identification, bot_id],
    async () => {
      const res = await HttpService.get(
        `/${customer_identification}/${entity_identification}/entity/widget/${bot_id}/call-bot`
      );
      return res?.data?.element;
    },
    {
      enabled: !!customer_identification && !!entity_identification && !!bot_id,
      onError: defaultOnError,
      ...options,
    }
  );
};

export const useDeleteWidgetCallbot = () => {
  return useMutation<any, AxiosError<any, any>, any>(
    ({
      entity_identification,
      customer_identification,
      bot_id,
    }: {
      entity_identification: string;
      customer_identification: string;
      bot_id: string;
    }) =>
      HttpService.delete(
        `/${customer_identification}/${entity_identification}/entity/widget/${bot_id}/call-bot`
      ),
    {
      onSuccess: (_res, variables) => {
        entityCache.delete(
          `entity_preferences_${variables.customer_identification}_${variables.entity_identification}`
        );
        queryClient.invalidateQueries(["entityPreferences"]);
        queryClient.invalidateQueries(["lmc-widget-bot"]);
      },
      onError: defaultOnError,
    }
  );
};

export const useCheckBadgesVerify = (onBadgeVerificationError?: () => void) => {
  return useMutation<any, AxiosError<any, any>, any>(
    ({
      entity_identification,
      customer_identification,
      user_identification,
    }: UpdateEntityProp) =>
      HttpService.get(
        `/${customer_identification}/${entity_identification}/entity/${user_identification}/badges-verify`
      ),
    {
      onSuccess: (res, variables) => {
        entityCache.delete(
          `entity_preferences_${variables.customer_identification}_${variables.entity_identification}`
        );
        queryClient.invalidateQueries(["entityPreferences"]);
        toast.success(res?.data?.message);
      },
      onError: (err) => {
        const errorCode = err.response?.data?.code;
        if (errorCode === "RSBVKO001") {
          if (onBadgeVerificationError) {
            onBadgeVerificationError();
          } else {
            toast.warn(err.response?.data?.message);
          }
        } else {
          defaultOnError(err);
        }
      },
    }
  );
};

export const usePlayGame = () => {
  return useMutation<any, AxiosError<any, any>, any>(
    ({
      entity_identification,
      user_identification,
      authToken,
    }: {
      entity_identification: string;
      user_identification: string;
      authToken: AxiosBasicCredentials;
    }) =>
      HttpService.post(
        `/entity/${entity_identification}/lmc/gaming/play/${user_identification}`,
        {},
        {
          auth: authToken,
        }
      ),
    { onError: defaultOnError }
  );
};

export const useCanCustomerPlay = (
  entity_identification: string,
  user_identification: string,
  authToken: AxiosBasicCredentials,
  options?: UseQueryOptions<
    any,
    AxiosError<any, any>,
    any,
    [string, string, string]
  >
) => {
  const { dispatch } = useLmcUserView();

  return useQuery(
    ["canCustomerPlay", entity_identification, user_identification],
    async () => {
      const res = await HttpService.get(
        `/entity/${entity_identification}/lmc/gaming/can-play/${user_identification}`,
        {
          auth: authToken,
        }
      );
      return res?.data?.element;
    },
    {
      enabled: !!entity_identification && !!user_identification,
      retry: (failureCount, error) => {
        if (error?.response?.data?.code === "RSUSKO026") return false;
        return failureCount < 3;
      },
      onError: (err) => {
        if (
          err?.response?.data?.code === "RSUSKO026" ||
          err?.response?.status === 401
        ) {
          dispatch({ type: "LOGOUT", payload: {} });
          return;
        }
        console.error(
          "Can play check failed:",
          err.response?.data?.message || err.message
        );
      },
      ...options,
    }
  );
};

export const useGetGameHistory = (
  customer_identification: string,
  entity_identification: string,
  user_identification: string,
  enabled = true
) => {
  return useQuery<GameHistoryRecord[], AxiosError<any, any>>(
    [
      "gameHistory",
      customer_identification,
      entity_identification,
      user_identification,
    ],
    async () => {
      const res = await HttpService.get(
        `/${customer_identification}/${entity_identification}/user/${user_identification}/data/${GAMING_HISTORY}`
      );
      const history = res?.data?.element?.user_data?.[GAMING_HISTORY];
      if (!history) return [];
      return Array.isArray(history) ? history : Object.values(history);
    },
    {
      enabled:
        enabled &&
        !!customer_identification &&
        !!entity_identification &&
        !!user_identification,
    }
  );
};

export const useGetEventProcessId = (
  customer_identification: string,
  entity_identification: string,
  event_code: string
) => {
  return useQuery(
    [
      "eventProcessId",
      customer_identification,
      entity_identification,
      event_code,
    ],
    async () => {
      const res = await HttpService.get(
        `/${customer_identification}/${entity_identification}/entity/process/${event_code}/ev/identification`
      );
      return res?.data;
    },
    {
      enabled:
        !!customer_identification && !!entity_identification && !!event_code,
      onError: defaultOnError,
    }
  );
};

export const useGetEvents = (
  customer_identification: string,
  entity_identification: string,
  options?: UseQueryOptions<
    any,
    AxiosError<any, any>,
    any,
    [string, string, string]
  >
) => {
  return useQuery(
    ["events", customer_identification, entity_identification],
    async () => {
      const res = await HttpService.get(
        `/${customer_identification}/${entity_identification}/event`
      );
      return res?.data?.element;
    },
    {
      enabled: !!customer_identification && !!entity_identification,
      onError: defaultOnError,
      ...options,
    }
  );
};

export const useGetEvent = (
  customer_identification: string,
  entity_identification: string,
  event_id: string,
  options?: UseQueryOptions<
    any,
    AxiosError<any, any>,
    any,
    [string, string, string, string]
  >
) => {
  return useQuery(
    ["event", customer_identification, entity_identification, event_id],
    async () => {
      const res = await HttpService.get(
        `/${customer_identification}/${entity_identification}/event/${event_id}`
      );
      return res?.data?.element;
    },
    {
      enabled:
        !!customer_identification && !!entity_identification && !!event_id,
      onError: defaultOnError,
      ...options,
    }
  );
};

export const useCreateEvent = () => {
  const { t } = useTranslation();
  return useMutation<any, AxiosError<any, any>, any>(
    ({
      customer_identification,
      entity_identification,
      eventData,
    }: {
      customer_identification: string;
      entity_identification: string;
      eventData: any;
    }) =>
      HttpService.post(
        `/${customer_identification}/${entity_identification}/event`,
        eventData
      ),
    {
      onSuccess: (res) => {
        queryClient.invalidateQueries(["events"]);
        toast.success(
          res?.data?.message ||
            (t("dashboard.events.createSuccess") as string) ||
            "Event created successfully"
        );
      },
      onError: defaultOnError,
    }
  );
};

export const useUpdateEvent = () => {
  const { t } = useTranslation();
  return useMutation<any, AxiosError<any, any>, any>(
    ({
      customer_identification,
      entity_identification,
      event_id,
      eventData,
    }: {
      customer_identification: string;
      entity_identification: string;
      event_id: string;
      eventData: any;
    }) =>
      HttpService.patch(
        `/${customer_identification}/${entity_identification}/event/${event_id}`,
        eventData
      ),
    {
      onSuccess: (res) => {
        queryClient.invalidateQueries(["events"]);
        queryClient.invalidateQueries(["event"]);
        toast.success(
          res?.data?.message ||
            (t("dashboard.events.updateSuccess") as string) ||
            "Event updated successfully"
        );
      },
      onError: defaultOnError,
    }
  );
};

export const useDeleteEvent = () => {
  const { t } = useTranslation();
  return useMutation<any, AxiosError<any, any>, any>(
    ({
      customer_identification,
      entity_identification,
      event_id,
    }: {
      customer_identification: string;
      entity_identification: string;
      event_id: string;
    }) =>
      HttpService.delete(
        `/${customer_identification}/${entity_identification}/event/${event_id}`
      ),
    {
      onSuccess: (res) => {
        queryClient.invalidateQueries(["events"]);
        toast.success(
          res?.data?.message ||
            (t("dashboard.events.deleteSuccess") as string) ||
            "Event deleted successfully"
        );
      },
      onError: defaultOnError,
    }
  );
};

export type EmailQuotaUsage = {
  tier: string | null;
  limit: number | null;
  used: number;
  remaining: number | null;
  period: string;
  resets_at: string;
};

export const useGetEmailQuota = (
  customer_identification: string,
  entity_identification: string,
  options?: UseQueryOptions<EmailQuotaUsage, AxiosError<any, any>>
) => {
  return useQuery<EmailQuotaUsage, AxiosError<any, any>>(
    [queryKeys.emailQuota, customer_identification, entity_identification],
    async () => {
      const res = await HttpService.get(
        `/${customer_identification}/${entity_identification}/entity/email-quota`
      );
      return res?.data?.element as EmailQuotaUsage;
    },
    {
      enabled: !!customer_identification && !!entity_identification,
      staleTime: 30 * 1000,
      onError: defaultOnError,
      ...options,
    }
  );
};

export const useGetGroupUsersCount = (
  customer_identification: string,
  entity_identification: string,
  group_identification: string | undefined,
  options?: UseQueryOptions<number, AxiosError<any, any>>
) => {
  return useQuery<number, AxiosError<any, any>>(
    [
      queryKeys.groupUsersCount,
      customer_identification,
      entity_identification,
      group_identification,
    ],
    async () => {
      const res = await HttpService.get(
        `/${customer_identification}/${entity_identification}/group/${group_identification}/users?limit=1&offset=0`
      );
      return (res?.data?.element?.pagination?.total as number) ?? 0;
    },
    {
      enabled:
        !!customer_identification &&
        !!entity_identification &&
        !!group_identification,
      staleTime: 30 * 1000,
      ...options,
    }
  );
};

export const useExportSearchUsersData = () => {
  return useMutation<any, AxiosError<any, any>, any>(
    ({
      customer_identification,
      entity_identification,
      searchBody,
    }: {
      customer_identification: string;
      entity_identification: string;
      searchBody?: any;
    }) =>
      HttpService.post(
        `/${customer_identification}/${entity_identification}/entity/users/search/export`,
        searchBody || {}
      ),
    {
      onError: defaultOnError,
    }
  );
};

export const useExportSearchPaymentsData = () => {
  return useMutation<any, AxiosError<any, any>, any>(
    ({
      customer_identification,
      entity_identification,
      searchBody,
    }: {
      customer_identification: string;
      entity_identification: string;
      searchBody?: any;
    }) =>
      HttpService.post(
        `/${customer_identification}/${entity_identification}/entity/payments/search/export`,
        searchBody || {}
      ),
    {
      onError: defaultOnError,
    }
  );
};
